import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import { TrustList } from "../components/TrustList";
import { Droplet, Wrench, ShowerHead, CheckCircle2 } from "lucide-react";
import { pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const plumbingSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Plumbing Services",
  provider: { "@type": "LocalBusiness", name: "TOFT Gas, Heating & Plumbing", telephone: "+447885592820" },
  areaServed: ["Rhondda Cynon Taf", "Cardiff", "Bridgend", "Caerphilly"],
  url: `${SITE_URL}/plumbing-services`,
};

const PlumbingServicesPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.plumbing} jsonLd={plumbingSchema} />
      <PageHero
        eyebrow="Plumbing Services"
        image={pageHeroImages.plumbing}
        title="Plumber Cardiff, Pontypridd, Porth, Bridgend &amp; Caerphilly"
        subtitle="From leaks and blockages to bathroom upgrades and rental property maintenance — trusted plumber across Cardiff, Pontypridd, Porth, Bridgend, Caerphilly and Rhondda Cynon Taf."
      />

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center">
                <Droplet className="h-6 w-6" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Plumbing Repairs
              </h2>
            </div>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
              From dripping taps to leaking pipework, we provide reliable plumbing repairs
              for homeowners and landlords across South Wales.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["Leaking taps and pipework", "Toilet repairs and replacements", "Blocked drains", "Burst pipe repairs", "Tap and washer replacements", "Outside taps"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center">
                <ShowerHead className="h-6 w-6" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Bathroom Plumbing
              </h2>
            </div>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
              Replacing a bathroom suite or upgrading a single appliance? We provide clean,
              professional plumbing for showers, baths, basins and toilets.
            </p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {["Shower fitting and replacement", "Bath and basin install", "Toilet fitting", "Waste and soil pipework", "Hot and cold supply"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-brand-blue-soft text-brand-blue flex items-center justify-center">
                <Wrench className="h-6 w-6" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Rental Property Plumbing
              </h2>
            </div>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              Many landlords use us for ongoing plumbing maintenance alongside their annual
              gas safety inspections. Reliable contractors, clear communication and
              landlord-friendly support across South Wales.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-7">
            Why Choose TOFT?
          </h2>
          <TrustList
            items={[
              "Gas Safe Registered",
              "Reliable Appointments",
              "Honest Advice",
              "Fair Pricing",
              "Trusted By Homeowners & Landlords",
              "Easy Contact By Phone, WhatsApp & Text",
            ]}
            columns={2}
          />
        </div>
      </section>

      <FinalCTA
        title="Need a Plumber in South Wales?"
        lines={["Call, WhatsApp or text today and we'll be in touch."]}
      />
    </>
  );
};

export default PlumbingServicesPage;
